import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Button, TextField, Autocomplete, Box } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// Стили для таблицы
const StyledTableCellSpec = styled(TableCell)(({ theme, darkMode }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: darkMode ? '#424242' : '#D0D0D0',
        color: darkMode ? '#f5f7fa' : 'black',
        padding: '4px 6px',
        fontSize: '12px',
        fontWeight: 'bold',
        lineHeight: 1.2,
        '&:hover': {
            backgroundColor: darkMode ? '#616161' : '#909090',
            cursor: 'pointer'
        },
        '@media (max-width: 768px)': {
            fontSize: '10px',
            padding: '2px 3px',
        }
    },
    [`&.${tableCellClasses.body}`]: {
        padding: '2px 6px',
        fontSize: '12px',
        lineHeight: 1.2,
        color: darkMode ? '#f5f7fa' : 'inherit',
        '@media (max-width: 768px)': {
            fontSize: '10px',
            padding: '1px 3px',
        }
    },
}));

const StyledTableCellSpecCat = styled(TableCell)(({ theme, darkMode }) => ({
    [`&.${tableCellClasses.body}`]: {
        padding: '2px 6px',
        fontSize: '12px',
        fontWeight: 'bold',
        lineHeight: 1.2,
        color: darkMode ? '#f5f7fa' : 'inherit',
        '@media (max-width: 768px)': {
            fontSize: '10px',
            padding: '1px 3px',
        }
    },
}));

const StyledTableRowSpec = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'allChecked' && prop !== 'darkMode'
})(({ theme, allChecked, darkMode }) => ({
    backgroundColor: darkMode
        ? (allChecked ? '#2e4a2e' : '#4a3e2e')
        : (allChecked ? '#e8f5e9' : '#fff3e0'),
}));

const ComplectExpanded = ({
    object,
    groupName,
    mode,
    specs,
    selectedItems,
    setSelectedItems,
    disabledItems,
    setDisabledItems,
    defaultSelectedItems,
    defaultDisabledItems,
    handleChangeType,
    smallMode,
    isExpanded,
    onToggle,
    searchText = '',
    darkMode = false,
    matchesSearch,
    matchesSearchRecursive,
    highlightText,
    expandFoundItems,
    getExpandedItemsForObject,
    typeChangedObjects,
    itemMetadata = {}
}) => {
    console.log(defaultDisabledItems, defaultSelectedItems)
    const [expandedItems, setExpandedItems] = useState([]);
    const searchMode = searchText.length > 0;
    
    // Получаем usersSettings из store
    const usersSettings = useSelector(state => state.state.usersSettings || []);
    
    // Функция для получения имени пользователя по userid
    const getUserName = (userid) => {
        if (!userid) return '-';
        const userIdNum = typeof userid === 'string' ? parseInt(userid) : userid;
        const user = usersSettings.find(u => u.id === userIdNum);
        return user?.name || userid;
    };
    
    // Convert searchText string to searchTerms array
    const searchTerms = searchText ? searchText.toLowerCase().split(/\s+/).filter(term => term.length > 0) : [];

    // Автоматическое раскрытие найденных элементов при поиске
    React.useEffect(() => {
        if (searchMode && getExpandedItemsForObject && object.mounted?.complect_filtered) {
            const result = getExpandedItemsForObject(object);
            setExpandedItems(result.expandedItems || []);
        } else if (!searchMode) {
            setExpandedItems([]);
        }
    }, [searchMode, searchText, getExpandedItemsForObject, object]);

    // Рекурсивный компонент для отображения элементов комплектации
    const RecursiveComplect = ({
        itemId,
        item,
        pillarId,
        pillarType,
        level = 0,
        darkMode = false
    }) => {
        const hasChildren = item.complect && item.complect.length > 0;
        const isExpanded = expandedItems.find(i => i.startsWith(itemId));
        // Элемент выбран, если он есть в selectedItems и НЕ в disabledItems
        const isChecked = selectedItems[pillarType]?.[pillarId]?.includes(itemId) &&
            !disabledItems[pillarType]?.[pillarId]?.includes(itemId);

        // Определение, является ли элемент неактивным в зависимости от режима
        const isItemDisabled = () => {
            if (mode) {
                // Mode = true: элементы из defaultSelectedItems неактивны, но только если тип опоры не был изменен
                return !typeChangedObjects?.hasOwnProperty(pillarId) && (defaultSelectedItems[pillarType]?.[pillarId]?.includes(itemId) || false);
            } else {
                // Mode = false: элементы из defaultDisabledItems неактивны
                return defaultDisabledItems[pillarType]?.[pillarId]?.includes(itemId) || false;
            }
        };

        // Определение промежуточного состояния (indeterminate) для чекбоксов с дочерними элементами
        const isIndeterminate = () => {
            if (!hasChildren) return false;
            const disables = disabledItems[pillarType]?.[pillarId];
            if (disables?.find(i => i === itemId)) return false;
            if (disables?.filter(i => i.startsWith(itemId + '_')).length === 0) return false;
            return true;
        };

        // const mass = item.mass ? item.mass.split(',').map(m => parseFloat(m).toFixed(2)) : [];
        let mass = (parseFloat(item.massa_1ed_kg) * parseFloat(item.count)).toString()
        mass = mass.includes('.') ? mass.split('.') : [mass]
        if (mass.length > 1) {
            mass[1] = mass[1].slice(0, 4)
        }
        // Стиль строки в зависимости от состояния
        const getRowStyle = () => {
            const baseStyle = {
                backgroundColor: darkMode
                    ? (isChecked ? '#2e4a2e' : '#4a3e2e')
                    : (isChecked ? '#e8f5e9' : '#fff3e0')
            };
            
            // Если элемент неактивен, делаем его приглушенным в зависимости от режима
            if (isItemDisabled()) {
                if (mode) {
                    // Mode = true: приглушенный зеленый цвет
                    return {
                        ...baseStyle,
                        backgroundColor: darkMode ? '#1a2a1a' : '#d4e6d4',
                        opacity: 0.7
                    };
                } else {
                    // Mode = false: приглушенный оранжевый цвет
                    return {
                        ...baseStyle,
                        backgroundColor: darkMode ? '#2a1f0a' : '#f0e6d2',
                        opacity: 0.7
                    };
                }
            }
            
            return baseStyle;
        };

        return (
            <>
                <StyledTableRowSpec style={getRowStyle()} darkMode={darkMode}>
                    <StyledTableCellSpec align='center' style={{ width: '28px', paddingLeft: `${level * 16}px` }} darkMode={darkMode}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                size="small"
                                disabled={isItemDisabled()}
                                sx={{
                                    padding: '2px',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: { xs: '14px', md: '16px' }
                                    },
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&.MuiCheckbox-indeterminate': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&:hover': {
                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                    },
                                    '&.Mui-disabled': {
                                        color: mode 
                                            ? (darkMode ? '#4a7c59' : '#6b8e6b') // Приглушенный зеленый для Mode = true
                                            : (darkMode ? '#8b6914' : '#b8860b'), // Приглушенный оранжевый для Mode = false
                                        opacity: 0.7
                                    }
                                }}
                                checked={isChecked}
                                indeterminate={isIndeterminate()}
                                onChange={(e) => {
                                    // Если элемент неактивен, не обрабатываем изменение
                                    if (isItemDisabled()) {
                                        return;
                                    }
                                    
                                    if (e.target.checked) {
                                        // При выборе: ищем элементы в disabledItems, которые нужно добавить в selectedItems
                                        const toAdd = disabledItems[pillarType]?.[pillarId]?.filter(i =>
                                            i === itemId || itemId.startsWith(i + '_') || i.startsWith(itemId + '_')
                                        ).filter(itemId => {
                                            if (mode) {
                                                // В режиме Mode = true не включаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                                                return typeChangedObjects?.hasOwnProperty(pillarId) || !defaultSelectedItems[groupName]?.[pillarId]?.includes(itemId);
                                            } else {
                                                // В режиме Mode = false не включаем элементы из defaultDisabledItems
                                                return !defaultDisabledItems[groupName]?.[pillarId]?.includes(itemId);
                                            }
                                        }) || [];

                                      

                                        setSelectedItems(prev => ({
                                            ...prev,
                                            [pillarType]: {
                                                ...prev[pillarType],
                                                [pillarId]: [
                                                    ...(prev[pillarType]?.[pillarId] || []).filter(i => !toAdd.includes(i)),
                                                    ...toAdd
                                                ]
                                            }
                                        }));

                                        setDisabledItems(prev => ({
                                            ...prev,
                                            [pillarType]: {
                                                ...prev[pillarType],
                                                [pillarId]: (prev[pillarType]?.[pillarId] || []).filter(i => !toAdd.includes(i))
                                            }
                                        }));
                                    } else {
                                        // При снятии: ищем элементы в selectedItems, которые нужно удалить
                                        let toRemove = selectedItems[pillarType]?.[pillarId]?.filter(i =>
                                            i === itemId || i.startsWith(itemId + '_')
                                        ).filter(itemId => {
                                            if (mode) {
                                                // В режиме Mode = true не снимаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                                                return typeChangedObjects?.hasOwnProperty(pillarId) || !defaultSelectedItems[groupName]?.[pillarId]?.includes(itemId);
                                            } else {
                                                // В режиме Mode = false не снимаем элементы из defaultDisabledItems
                                                return !defaultDisabledItems[groupName]?.[pillarId]?.includes(itemId);
                                            }
                                        }) || [];
                                        
                                        // Проверяем всех прародителей и снимаем галочки с тех, у которых больше нет выбранных дочерних элементов
                                        const checkAndRemoveParents = (currentItemId) => {
                                            const parentId = currentItemId.split('_').slice(0, -1).join('_');
                                            if (!parentId) return; // Если это корневой элемент, выходим
                                            
                                            // Получаем все дочерние элементы родителя
                                            const parentChildren = selectedItems[pillarType]?.[pillarId]?.filter(i => 
                                                i.startsWith(parentId + '_') && !i.substring(parentId.length + 1).includes('_')
                                            ) || [];
                                            
                                            // Проверяем, есть ли у родителя выбранные дочерние элементы (не в toRemove)
                                            const remainingSelectedChildren = parentChildren.filter(childId => 
                                                !toRemove.includes(childId) && 
                                                !disabledItems[pillarType]?.[pillarId]?.includes(childId)
                                            );
                                            
                                            // Если у родителя больше нет выбранных дочерних элементов, добавляем его в toRemove
                                            if (remainingSelectedChildren.length === 0 && !toRemove.includes(parentId)) {
                                                toRemove.push(parentId);
                                                // Рекурсивно проверяем прародителя
                                                checkAndRemoveParents(parentId);
                                            }
                                        };
                                        
                                        // Проверяем всех прародителей
                                        checkAndRemoveParents(itemId);

                                        setSelectedItems(prev => ({
                                            ...prev,
                                            [pillarType]: {
                                                ...prev[pillarType],
                                                [pillarId]: (prev[pillarType]?.[pillarId] || []).filter(i => !toRemove.includes(i))
                                            }
                                        }));

                                        setDisabledItems(prev => ({
                                            ...prev,
                                            [pillarType]: {
                                                ...prev[pillarType],
                                                [pillarId]: [
                                                    ...(prev[pillarType]?.[pillarId] || []).filter(i => !toRemove.includes(i)),
                                                    ...toRemove
                                                ]
                                            }
                                        }));
                                    }
                                }}
                            />
                        </div>
                    </StyledTableCellSpec>
                    <StyledTableCellSpec darkMode={darkMode}>
                        {hasChildren && (
                            <Button
                                size="small"
                                onClick={() => setExpandedItems(prev => {
                                    if (prev.find(i => i.startsWith(itemId))) {
                                        return prev.filter(i => !i.startsWith(itemId))
                                    } else {
                                        return [...prev, itemId]
                                    }
                                })}
                                style={{ minWidth: '20px', padding: '2px' }}
                                sx={{
                                    '& .MuiSvgIcon-root': { fontSize: { xs: '12px', md: '14px' } },
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    '&:hover': {
                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                    }
                                }}
                            >
                                {isExpanded ? <ArrowDownwardIcon fontSize="small" /> : <ArrowForwardIosSharpIcon fontSize="small" />}
                            </Button>
                        )}
                    </StyledTableCellSpec>
                    <StyledTableCellSpec darkMode={darkMode}>
                        {searchMode ? highlightText(item.name, searchTerms) : item.name}
                    </StyledTableCellSpec>
                    <StyledTableCellSpec darkMode={darkMode}>
                        {searchMode ? highlightText(item.type_marka_docs, searchTerms) : item.type_marka_docs}
                    </StyledTableCellSpec>
                    <StyledTableCellSpec darkMode={darkMode}>
                        {searchMode ? highlightText(item.factory_manufacturer, searchTerms) : item.factory_manufacturer}
                    </StyledTableCellSpec>
                    <StyledTableCellSpec  align='center' darkMode={darkMode}>{parseFloat(item.count).toString().replace('.', ',')} {item.unit}</StyledTableCellSpec>
                    <StyledTableCellSpec  align='center' darkMode={darkMode}>{mass.join(',')}</StyledTableCellSpec>
                    <StyledTableCellSpec  align='center' darkMode={darkMode}>
                        {(() => {
                            const metadata = itemMetadata[pillarType]?.[pillarId]?.[itemId];
                            if (metadata?.date) {
                                const date = new Date(metadata.date);
                                const time = date.toLocaleTimeString('ru-RU', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false
                                });
                                const dateStr = date.toLocaleDateString('ru-RU', { 
                                    year: 'numeric', 
                                    month: '2-digit', 
                                    day: '2-digit' 
                                });
                                return `${time} ${dateStr}`;
                            }
                            return '-';
                        })()}
                    </StyledTableCellSpec>
                    <StyledTableCellSpec  align='center' darkMode={darkMode}>
                        {(() => {
                            const metadata = itemMetadata[pillarType]?.[pillarId]?.[itemId];
                            return getUserName(metadata?.userid);
                        })()}
                    </StyledTableCellSpec>
                </StyledTableRowSpec>
                {/* Рекурсивный рендер дочерних элементов */}
                {hasChildren && isExpanded && item.complect
                    .filter(child => !searchMode || matchesSearchRecursive(child, searchTerms))
                    .map((child, index) => (
                        <RecursiveComplect
                            key={`${itemId}_${child.id}`}
                            itemId={`${itemId}_${child.id}`}
                            item={child}
                            pillarId={pillarId}
                            pillarType={pillarType}
                            level={level + 1}
                            darkMode={darkMode}
                        />
                    ))}
            </>
        );
    };

    // Функция генерации комплектации
    const getCompl = () => {
        // Инициализируем массивы, если их нет
        if (!selectedItems[groupName]?.[object.fid]) {
            // Рекурсивная функция для инициализации всех элементов комплектации
            const initializeComplectItems = (complect, id = '') => {
                const items = [];
                if (complect) {
                    complect.forEach(c => {
                        const itemId = id ? `${id}_${c.id}` : `${c.id}`;
                        items.push(itemId);
                        if (c.complect) {
                            items.push(...initializeComplectItems(c.complect, itemId));
                        }
                    });
                }
                return items;
            };

            const allItems = [];
            if (object.mounted?.complect_filtered) {
                object.mounted.complect_filtered.forEach(cf => {
                    allItems.push(...initializeComplectItems(cf.complect));
                });
            }

            // Инициализируем все элементы как выбранные (в selectedItems)
            setSelectedItems(prev => ({
                ...prev,
                [groupName]: {
                    ...prev[groupName],
                    [object.fid]: allItems
                }
            }));
        }
        if (!disabledItems[groupName]?.[object.fid]) {
            // Инициализируем пустой массив для отключенных элементов
            setDisabledItems(prev => ({
                ...prev,
                [groupName]: {
                    ...prev[groupName],
                    [object.fid]: []
                }
            }));
        }

        const currentSelectedItems = selectedItems[groupName]?.[object.fid] || [];
        const currentDisabledItems = disabledItems[groupName]?.[object.fid] || [];

        return (
            <TableContainer component={Paper} sx={{
                marginBottom: '4px',
                backgroundColor: darkMode ? '#1e1e1e' : 'inherit',
                '& .MuiTable-root': {
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                },
                // Кастомные стили скролла
                '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: darkMode ? '#2a2a2a' : '#f1f1f1',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: darkMode ? '#555' : '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                        background: darkMode ? '#777' : '#a8a8a8',
                    },
                },
                '&::-webkit-scrollbar-corner': {
                    background: darkMode ? '#2a2a2a' : '#f1f1f1',
                },
                // Для Firefox
                scrollbarWidth: 'thin',
                scrollbarColor: darkMode ? '#555 #2a2a2a' : '#c1c1c1 #f1f1f1',
            }}>
                <Table size="small" sx={{
                    minWidth: smallMode ? 'auto' : 800,
                    '& .MuiTableCell-root': {
                        borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
                        borderRight: darkMode ? '1px solid rgba(255, 255, 255, 0.03)' : '1px solid rgba(0, 0, 0, 0.05)',
                    },
                    '& .MuiTableCell-root:last-child': {
                        borderRight: 'none',
                    }
                }} aria-label="simple table">
                    <TableHead>
                        <StyledTableRowSpec darkMode={darkMode}>
                            <StyledTableCellSpec align="center" style={{ width: '28px' }} darkMode={darkMode}>
                                <Checkbox
                                    size="small"
                                    sx={{
                                        padding: '2px',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: { xs: '14px', md: '16px' }
                                        },
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        '&.Mui-checked': {
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        }
                                    }}
                                    checked={currentDisabledItems.length === 0}
                                    indeterminate={currentSelectedItems.length !== 0 && currentDisabledItems.length !== 0}
                                    onChange={(e) => {
                                        if ((!mode && currentSelectedItems.length === 0) || (mode && currentSelectedItems.filter(selectedItem => !defaultSelectedItems[groupName]?.[object.fid]?.includes(selectedItem)).length === 0)) {
                                            // Выбираем все - перемещаем все из disabledItems в selectedItems
                                            // Но только те элементы, которые не заблокированы в зависимости от режима
                                            const toEnable = currentDisabledItems.filter(itemId => {
                                                if (mode) {
                                                    // В режиме Mode = true не включаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                                                    return typeChangedObjects?.hasOwnProperty(object.fid) || !defaultSelectedItems[groupName]?.[object.fid]?.includes(itemId);
                                                } else {
                                                    // В режиме Mode = false не включаем элементы из defaultDisabledItems
                                                    return !defaultDisabledItems[groupName]?.[object.fid]?.includes(itemId);
                                                }
                                            });
                                            
                                            setSelectedItems(prev => ({
                                                ...prev,
                                                [groupName]: {
                                                    ...prev[groupName],
                                                    [object.fid]: [...currentSelectedItems, ...toEnable]
                                                }
                                            }));
                                            setDisabledItems(prev => ({
                                                ...prev,
                                                [groupName]: {
                                                    ...prev[groupName],
                                                    [object.fid]: currentDisabledItems.filter(itemId => !toEnable.includes(itemId))
                                                }
                                            }));
                                        } else {
                                            // Снимаем все - перемещаем все из selectedItems в disabledItems
                                            // Но только те элементы, которые не заблокированы в зависимости от режима
                                            const toDisable = currentSelectedItems.filter(itemId => {
                                                if (mode) {
                                                    // В режиме Mode = true не снимаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                                                    return typeChangedObjects?.hasOwnProperty(object.fid) || !defaultSelectedItems[groupName]?.[object.fid]?.includes(itemId);
                                                } else {
                                                    // В режиме Mode = false не снимаем элементы из defaultDisabledItems
                                                    return !defaultDisabledItems[groupName]?.[object.fid]?.includes(itemId);
                                                }
                                            });
                                            
                                            setSelectedItems(prev => ({
                                                ...prev,
                                                [groupName]: {
                                                    ...prev[groupName],
                                                    [object.fid]: currentSelectedItems.filter(itemId => !toDisable.includes(itemId))
                                                }
                                            }));
                                            setDisabledItems(prev => ({
                                                ...prev,
                                                [groupName]: {
                                                    ...prev[groupName],
                                                    [object.fid]: [...currentDisabledItems, ...toDisable]
                                                }
                                            }));
                                        }
                                    }}
                                />
                            </StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}></StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Наименование</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Тип, марка, обозначение документа, опросного листа</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Завод изготовитель</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Количество</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Масса ед., кг.</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Дата</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Пользователь</StyledTableCellSpec>
                        </StyledTableRowSpec>
                    </TableHead>
                    <TableBody>
                        {object.mounted.complect_filtered?.map(cf => {
                            // Фильтруем элементы категории по поисковому запросу
                            const filteredItems = cf.complect.filter(item =>
                                !searchMode || matchesSearchRecursive(item, searchTerms)
                            );

                            // Если в режиме поиска и нет найденных элементов, не показываем категорию
                            if (searchMode && filteredItems.length === 0) {
                                return null;
                            }

                            // Категория выбрана, если все её элементы выбраны (есть в selectedItems и нет в disabledItems)
                            const categoryChecked = cf.complect.every(c => {
                                const itemId = `${c.id}`;
                                return currentSelectedItems?.includes(itemId) && !currentDisabledItems?.includes(itemId);
                            });

                            return (
                                <React.Fragment key={cf.type}>
                                    <StyledTableRowSpec darkMode={darkMode}>
                                        <StyledTableCellSpecCat
                                            colSpan={9}
                                            align="center"
                                            darkMode={darkMode}
                                            style={{
                                                fontWeight: 'bold',
                                                backgroundColor: darkMode
                                                    ? (categoryChecked ? '#1a3a1a' : '#2d2418')
                                                    : (categoryChecked ? '#c8e6c9' : '#ffe0b2'),
                                                padding: '2px 6px'
                                            }}
                                        >
                                            {cf.type}
                                        </StyledTableCellSpecCat>
                                    </StyledTableRowSpec>
                                    {filteredItems.map((item, index) => (
                                        <RecursiveComplect
                                            key={`${object.fid}_${item.id}`}
                                            itemId={`${item.id}`}
                                            item={item}
                                            pillarId={object.fid}
                                            pillarType={groupName}
                                            darkMode={darkMode}
                                        />
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // Подготовка данных для Autocomplete
    const getGroupedOptions = () => {
        if (groupName === 'pillars') {
            const jointPoles = specs.pillars
                .filter(t => t.category?.includes('0,4') && t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Опоры совместного подвеса' }));

            const lowVoltagePoles = specs.pillars
                .filter(t => t.category?.includes('0,4') && !t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Опоры 0,4кв' }));

            const highVoltagePoles = specs.pillars
                .filter(t => !t.category?.includes('0,4') && t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Опоры 6(10)кв' }));

            const otherPoles = specs.pillars
                .filter(t => !t.category?.includes('0,4') && !t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Другие опоры' }));

            return [...jointPoles, ...lowVoltagePoles, ...highVoltagePoles, ...otherPoles];
        }
        if (groupName === 'zvz') {
            return specs.zvz.map(t => ({ ...t, group: 'Адаптеры ВЗ' }));
        }
        if (groupName === 'opn') {
            return specs.opn.map(t => ({ ...t, group: 'ОПН' }));
        }
        if (groupName === 'rlnd') {
            return specs.rlnd.map(t => ({ ...t, group: 'РЛНД' }));
        }
        if (groupName === 'transformers') {
            return specs.transformers?.map(t => ({ ...t, group: 'Трансформаторы' })) || [];
        }
        if (groupName === 'runn') {
            return specs.runn?.map(t => ({ ...t, group: 'РУНН' })) || [];
        }
        return [];
    };

    // if (!mode) return null;

    const options = getGroupedOptions();
    const currentValue = options.find(opt => opt.id === object.mounted?.id) || null;

    return (
        <Box sx={{
            p: 1,
            backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e0e0e0',
            borderRadius: '4px',
            maxHeight: '400px',
            overflow: 'auto',
            // Кастомные стили скролла
            '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
            },
            '&::-webkit-scrollbar-track': {
                background: darkMode ? '#2a2a2a' : '#f1f1f1',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
                background: darkMode ? '#555' : '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                    background: darkMode ? '#777' : '#a8a8a8',
                },
            },
            '&::-webkit-scrollbar-corner': {
                background: darkMode ? '#2a2a2a' : '#f1f1f1',
            },
            // Для Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: darkMode ? '#555 #2a2a2a' : '#c1c1c1 #f1f1f1',
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px',
                flexWrap: 'wrap',
                gap: '4px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        size="small"
                        onClick={onToggle}
                        sx={{
                            minWidth: 'auto',
                            padding: '2px',
                            color: darkMode ? '#64b5f6' : '#1976d2',
                            '&:hover': {
                                backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                            },
                            '& .MuiSvgIcon-root': {
                                fontSize: { xs: '14px', md: '16px' }
                            }
                        }}
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </Button>
                    <span style={{
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                        color: darkMode ? '#f5f7fa' : '#333'
                    }}>Комплектация</span>
                </Box>

                {/* Селектор типа объекта */}
                {specs[groupName] && specs[groupName].length > 1 && (
                    <Autocomplete
                        size="small"
                        disabled={!mode} // Блокируем в режиме Mode = false
                        sx={{
                            minWidth: { xs: 250, md: 400 },
                            '& .MuiInputBase-input': {
                                color: darkMode ? '#f5f7fa !important' : '#333 !important',
                                '-webkit-text-fill-color': darkMode ? '#f5f7fa !important' : '#333 !important',
                                
                            },
                            '& .MuiInputLabel-root': {
                                color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                            },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                '& fieldset': {
                                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                },
                                '&:hover fieldset': {
                                    borderColor: darkMode ? 'rgba(100, 181, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: darkMode ? '#2a1f0a' : '#f0e6d2',
                                    '-webkit-text-fill-color': darkMode ? '#f5f7fa !important' : '#333 !important',
                                    opacity: 0.7,
                                    '& fieldset': {
                                        borderColor: darkMode ? '#8b6914' : '#b8860b',
                                    }
                                }
                            },
                            '& .MuiAutocomplete-endAdornment': {
                                color: darkMode ? '#bbb !important' : 'rgba(0,0,0,0.54) !important',
                            },
                            '& .MuiAutocomplete-clearIndicator': {
                                color: darkMode ? '#bbb !important' : 'rgba(0,0,0,0.54) !important',
                                '&:hover': {
                                    color: darkMode ? '#f5f7fa !important' : 'rgba(0,0,0,0.87) !important',
                                },
                            },
                            '& .MuiAutocomplete-popupIndicator': {
                                color: darkMode ? '#bbb !important' : 'rgba(0,0,0,0.54) !important',
                                '&:hover': {
                                    color: darkMode ? '#64b5f6 !important' : '#1976d2 !important',
                                },
                            },
                        }}
                        options={options}
                        groupBy={(option) => option.group}
                        getOptionLabel={(option) => option.name || ''}
                        value={currentValue}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                // Сначала обновляем тип объекта
                                handleChangeType(newValue.id, groupName, object);

                                // Затем обновляем чекбоксы для нового типа
                                const newSpec = specs[groupName]?.find(t => t.id === newValue.id);
                                if (newSpec?.complect_filtered) {
                                    const allItems = [];

                                    // Рекурсивная функция для сбора всех элементов комплектации
                                    const collectAllItems = (complect, id = '') => {
                                        if (complect) {
                                            complect.forEach(c => {
                                                const itemId = id ? `${id}_${c.id}` : `${c.id}`;
                                                allItems.push(itemId);
                                                if (c.complect) {
                                                    collectAllItems(c.complect, itemId);
                                                }
                                            });
                                        }
                                    };

                                    // Собираем все элементы из всех категорий нового типа
                                    newSpec.complect_filtered.forEach(cf => {
                                        collectAllItems(cf.complect);
                                    });

                                    // Устанавливаем все элементы как выбранные
                                    setSelectedItems(prev => ({
                                        ...prev,
                                        [groupName]: {
                                            ...prev[groupName],
                                            [object.fid]: allItems
                                        }
                                    }));

                                    // Очищаем отключенные элементы
                                    setDisabledItems(prev => ({
                                        ...prev,
                                        [groupName]: {
                                            ...prev[groupName],
                                            [object.fid]: []
                                        }
                                    }));
                                }
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={`Тип ${groupName === 'transformers' ? 'трансформатора' : groupName === 'pillars' ? 'опоры' : groupName === 'zvz' ? 'адаптера ВЗ' : groupName === 'opn' ? 'ОПН' : groupName === 'rlnd' ? 'РЛНД' : groupName === 'runn' ? 'РУНН' : ''}`}
                                placeholder={`Выберите тип ${groupName === 'transformers' ? 'трансформатора' : groupName === 'pillars' ? 'опоры' : groupName === 'zvz' ? 'адаптера ВЗ' : groupName === 'opn' ? 'ОПН' : groupName === 'rlnd' ? 'РЛНД' : groupName === 'runn' ? 'РУНН' : ''}`}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                        color: darkMode ? '#f5f7fa' : '#333',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                        '& fieldset': {
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? 'rgba(100, 181, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                    },
                                }}
                            />
                        )}
                        slotProps={{
                            paper: {
                                sx: {
                                    zIndex: 9999,
                                    backgroundColor: darkMode ? '#1e1e1e' : '#fff',
                                    '& .MuiAutocomplete-option': {
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                        padding: { xs: '4px 8px', md: '6px 12px' },
                                        minHeight: { xs: '28px', md: '32px' },
                                        color: darkMode ? '#f5f7fa' : 'inherit',
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        }
                                    },
                                    '& .MuiAutocomplete-groupLabel': {
                                        fontSize: { xs: '0.9rem', md: '1rem' },
                                        padding: { xs: '4px 8px', md: '6px 12px' },
                                        lineHeight: 1.2,
                                        fontWeight: 'bold',
                                        color: darkMode ? '#f5f7fa' : 'black',
                                        backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
                                        borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)'
                                    }
                                }
                            },
                            listbox: {
                                style: {
                                    maxHeight: '300px',
                                    fontSize: { xs: '0.8rem', md: '0.9rem' }
                                }
                            }
                        }}
                        disablePortal
                        noOptionsText="Нет подходящих вариантов"
                        loadingText="Загрузка..."
                    />
                )}
            </Box>

            {/* Содержимое комплектации */}
            {getCompl()}
        </Box>
    );
};

export default ComplectExpanded;
